import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { Mail, Building, Clock, BookOpen, FileText, GraduationCap, Rocket, Award, Map, Trophy, Layers, MessageSquare, FlaskConical, Globe, Briefcase, Calendar, Handshake, FolderOpen, Download, Heart, ShoppingBag, Wrench, Ticket, Users, HelpCircle, AlertTriangle, Target, Anchor, Gamepad2, Compass, Lock, CheckCircle, Flag, Gift, Cloud } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

import { API } from '../config/api';

// Icon mapping for footer links
const iconMap = {
  Building, Clock, BookOpen, FileText, Mail, GraduationCap, Rocket, Award, Map, Trophy,
  Layers, MessageSquare, FlaskConical, Globe, Briefcase, Calendar, Handshake,
  FolderOpen, Download, Heart, ShoppingBag, Wrench, Ticket, Users, HelpCircle, AlertTriangle,
  Target, Anchor, Gamepad2, Compass, Flag, Gift, Cloud
};

const Footer = () => {
  const { user, isAuthenticated, squidTourStatus } = useAuth();
  const navigate = useNavigate();
  const [pageVisibility, setPageVisibility] = useState([]);
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);
  
  // Fetch page visibility to filter hidden pages
  useEffect(() => {
    const fetchPageVisibility = async () => {
      try {
        // Use publicApi since visibility doesn't require auth
        const response = await publicApi.get('/pages/visibility', {
          timeout: 2000  // 2 second timeout
        });
        
        const pages = response.data.pages || [];
        setPageVisibility(pages);
        setVisibilityLoaded(true);
      } catch (error) {
        console.log('Footer visibility check skipped');
        setVisibilityLoaded(true); // Still mark as loaded to avoid infinite loading
      }
    };
    fetchPageVisibility();
  }, []);
  
  // Get squidTour completion status from AuthContext
  const squidTourComplete = squidTourStatus?.completed ?? true;

  const isUserAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isDebugger = user?.is_debugger === true;

  // Check if a page is published (for nav/footer visibility)
  // Pages not in the visibility system are shown by default (not controlled)
  // Only hide pages that are explicitly set to is_published=false
  const isPagePublished = (path) => {
    // Find the page in the visibility list
    const page = pageVisibility.find(p => {
      // Exact match (including query params)
      if (p.path === path) return true;
      // Check base paths without query params
      const itemBasePath = path.split('?')[0];
      const pageBasePath = p.path.split('?')[0];
      if (pageBasePath === itemBasePath) return true;
      return false;
    });
    
    // If page not in visibility system, assume it's visible (not controlled)
    if (!page) return true;
    
    // Only check is_published - no admin bypass for menus
    return page.is_published === true;
  };

  // Check if a page should show in footer
  const shouldShowInFooter = (path) => {
    // Show all links immediately while visibility data is loading
    // This prevents the footer from appearing empty/delayed
    if (!visibilityLoaded) return true;
    
    const page = pageVisibility.find(p => {
      if (p.path === path) return true;
      const itemBasePath = path.split('?')[0];
      const pageBasePath = p.path.split('?')[0];
      if (pageBasePath === itemBasePath) return true;
      return false;
    });
    
    // If page not in visibility system, show it (not controlled)
    if (!page) return true;
    // Must be published and show_in_footer
    return page.is_published === true && page.show_in_footer !== false;
  };

  // SquidTour check - SUSPENDED (Jan 2026)
  // All content is accessible without tour completion
  const isSquidTourLocked = (path) => {
    return false; // Tour suspended - nothing is locked
  };

  // Subscription tier requirements - DISABLED (Pay-As-You-Go model)
  // All pages accessible after SquidTour completion
  const getPageSubscriptionStatus = (path) => {
    return { hasAccess: true, requiredTier: null };
  };

  const handleLinkClick = (e, path) => {
    // Only check SquidTour lock for learning content
    if (isSquidTourLocked(path)) {
      e.preventDefault();
      toast.error('🔒 Complete SquidTour to Start Learning!', {
        description: 'Complete the quick orientation first, then you can access your course content.',
        action: { label: 'Go to SquidTour', onClick: () => navigate('/worlds/squidtour') }
      });
      return false;
    }
    return true;
  };

  // Footer categories - 6 columns organized by user journey
  const footerCategories = [
    {
      title: 'Learn',
      links: [
        { name: 'SquidCourse', path: '/courses', icon: 'BookOpen' },
        { name: 'SquidCamps', path: '/bootcamps', icon: 'GraduationCap' },
        { name: 'SquidPrep', path: '/cert-prep', icon: 'Award' },
        { name: 'SquidLabs', path: '/labs', icon: 'FlaskConical' },
        { name: 'SquidCards', path: '/flashcards', icon: 'Layers' },
        { name: 'SquidStudy', path: '/study', icon: 'BookOpen' },
      ]
    },
    {
      title: 'Practice',
      links: [
        { name: 'SquidCloud', path: '/squidcloud', icon: 'Cloud' },
        { name: 'Capture the Squid', path: '/capture-the-squid', icon: 'Target' },
        { name: 'SquidPardy', path: '/squidpardy', icon: 'Gamepad2' },
        { name: 'SquidTrench', path: '/squidtrench', icon: 'Anchor' },
        { name: 'SquidReady', path: '/interview-prep', icon: 'Users' },
        { name: 'SquidWorlds', path: '/worlds', icon: 'Compass' },
      ]
    },
    {
      title: 'Career',
      links: [
        { name: 'SquidJobs', path: '/jobs', icon: 'Briefcase' },
        { name: 'SquidIntern', path: '/internships', icon: 'GraduationCap' },
        { name: 'SquidFolio', path: '/resume-lab', icon: 'FileText' },
        { name: 'SquidGuide', path: '/mentorship', icon: 'Users' },
        { name: 'SquidMap', path: '/roadmap', icon: 'Map' },
        { name: 'SquidCerts', path: '/certificates', icon: 'Award' },
      ]
    },
    {
      title: 'Community',
      links: [
        { name: 'SquidEvents', path: '/events', icon: 'Calendar' },
        { name: 'SquidNews', path: '/blog', icon: 'FileText' },
        { name: 'SquidPedia', path: '/squidpedia', icon: 'BookOpen' },
        { name: 'SquidLeaders', path: '/leaderboard', icon: 'Trophy' },
        { name: 'SquidFriends', path: '/partnership', icon: 'Handshake' },
        { name: 'SquidTeach', path: '/teach', icon: 'GraduationCap' },
      ]
    },
    {
      title: 'Marketplace',
      links: [
        { name: 'SquidMerch', path: '/shop', icon: 'ShoppingBag' },
        { name: 'SquidCoins', path: '/buy-coins', icon: 'Coins' },
        { name: 'SquidVouch', path: '/vouchers', icon: 'Gift' },
        { name: 'SquidDrive', path: '/library', icon: 'FolderOpen' },
        { name: 'SquidTools', path: '/tools', icon: 'Wrench' },
        { name: 'SquidGives', path: '/donate', icon: 'Heart' },
      ]
    },
    {
      title: 'Platform',
      links: [
        { name: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
        { name: 'Booking', path: '/booking', icon: 'CalendarCheck' },
        { name: 'SquidFamily', path: '/about', icon: 'Building' },
        { name: 'SquidHelp', path: '/support', icon: 'HelpCircle' },
        { name: 'SquidTickets', path: '/helpdesk', icon: 'Ticket' },
        { name: 'SquidContact', path: '/contact', icon: 'Mail' },
      ]
    }
  ];
  
  return (
    <footer className="bg-card border-t border-border mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* Top Row: 6 Categories with Links - Centered */}
        <div className="border-b border-border pb-8 mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 max-w-5xl mx-auto">
            {footerCategories.map((category, index) => (
              <div key={category.title} className="space-y-3">
                <h3 className="font-bold text-primary uppercase tracking-wider text-xs flex items-center gap-2 border-b border-primary/20 pb-2">
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.links
                    .filter(link => {
                      // STRICT: Only show published pages in footer
                      return shouldShowInFooter(link.path);
                    })
                    .map((link) => {
                    // Public pages that are always accessible - NEVER locked
                    // Added /bootcamps, /courses, and /cert-prep as public pages
                    const publicPaths = ['/squidmember', '/partnership', '/squidbug', '/donate', '/shop', '/login', '/contact', '/about', '/blog', '/internships', '/schedule', '/capture-the-squid', '/worlds/squidtour', '/support', '/help', '/helpdesk', '/bootcamps', '/courses', '/cert-prep', '/events'];
                    const isPublic = publicPaths.includes(link.path);
                    
                    // For public links - green check on LEFT, no locks ever
                    if (isPublic) {
                      return (
                        <li key={link.path}>
                          <Link 
                            to={link.path}
                            className="text-sm transition-colors flex items-center gap-2 text-muted-foreground hover:text-primary"
                          >
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <span>{link.name}</span>
                          </Link>
                        </li>
                      );
                    }
                    
                    // For non-public links - check locks/access
                    // Use isAuthenticated from AuthContext for reliable login check
                    const notLoggedIn = !isAuthenticated;
                    const isTourLocked = isSquidTourLocked(link.path);
                    const { hasAccess: hasSubAccess, requiredTier } = getPageSubscriptionStatus(link.path);
                    const showSubLock = isAuthenticated && !hasSubAccess && requiredTier;
                    const showCheck = isAuthenticated && hasSubAccess && !isTourLocked;
                    const isLocked = notLoggedIn || isTourLocked || showSubLock;
                    
                    return (
                      <li key={link.path} className="relative group">
                        <Link 
                          to={notLoggedIn ? '/login' : link.path}
                          onClick={(e) => !notLoggedIn && handleLinkClick(e, link.path)}
                          className={`text-sm transition-colors flex items-center gap-2 ${isLocked ? 'text-muted-foreground/70 hover:text-muted-foreground' : 'text-muted-foreground hover:text-primary'}`}
                        >
                          {/* Red lock for non-logged-in users */}
                          {notLoggedIn && <Lock className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          {/* Tour locked indicator for logged-in users */}
                          {!notLoggedIn && isTourLocked && <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                          {/* Subscription lock for logged-in users */}
                          {!notLoggedIn && !isTourLocked && showSubLock && <Lock className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          {/* Blue check for accessible pages (logged-in users) */}
                          {!notLoggedIn && !isTourLocked && showCheck && <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                          <span>{link.name}</span>
                        </Link>
                        {/* Tooltip for non-logged-in users */}
                        {notLoggedIn && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                            Login required
                          </div>
                        )}
                        {/* Tooltip for subscription locked items */}
                        {!notLoggedIn && showSubLock && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                            Requires {requiredTier}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
        
        {/* Middle Row: Centered Tagline */}
        <div className="text-center py-6 border-b border-border mb-6">
          <Link to="/" className="inline-flex items-center space-x-2 mb-3">
            <picture>
              <source srcSet="/squiddy_logo.webp" type="image/webp" />
              <img 
                src="/squiddy_logo.png" 
                alt="Squidemy Logo" 
                className="w-16 h-16 object-contain"
                width="64"
                height="64"
                loading="lazy"
              />
            </picture>
            <div className="flex flex-col items-start">
              <span className="text-2xl font-bold text-black dark:text-white" style={{ fontFamily: "'AstroSpace', sans-serif", letterSpacing: '0.05em' }}>
                SQUIDEMY
              </span>
              <span 
                className="text-[10px] font-bold uppercase"
                style={{ background: 'linear-gradient(90deg, #06b6d4, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '0.1em' }}
              >
                IT & Cybersecurity Training
              </span>
            </div>
          </Link>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Empowering IT careers through accessible, quality education.
          </p>
        </div>
        
        {/* Third Row: Social Icons (centered) + Newsletter (right) */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 border-b border-border pb-6 mb-6">
          {/* Left/Center: Social Media Icons - 32x32 centered */}
          <div className="flex items-center justify-center gap-4">
            <a href="https://discord.gg/QRv8hpn9gk" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#5865F2] transition-colors p-2 hover:bg-accent rounded-xl" aria-label="Discord">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61584548987568" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#1877F2] transition-colors p-2 hover:bg-accent rounded-xl" aria-label="Facebook">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="https://x.com/squidemy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors p-2 hover:bg-accent rounded-xl" aria-label="X (Twitter)">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/squidemy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#0A66C2] transition-colors p-2 hover:bg-accent rounded-xl" aria-label="LinkedIn">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href="https://www.youtube.com/@squidemy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#FF0000] transition-colors p-2 hover:bg-accent rounded-xl" aria-label="YouTube">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            <a href="https://www.tiktok.com/@squidemy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors p-2 hover:bg-accent rounded-xl" aria-label="TikTok">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/squidemy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#E4405F] transition-colors p-2 hover:bg-accent rounded-xl" aria-label="Instagram">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
            <a href="https://www.twitch.tv/squidemy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#9146FF] transition-colors p-2 hover:bg-accent rounded-xl" aria-label="Twitch">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
              </svg>
            </a>
            <a href="https://bsky.app/profile/squidemy.bsky.social" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#0085FF] transition-colors p-2 hover:bg-accent rounded-xl" aria-label="Bluesky">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
              </svg>
            </a>
          </div>
          
          {/* Right: SquidLetter with wider input */}
          <div className="flex-1 max-w-md w-full md:w-auto">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-muted-foreground flex-shrink-0">SquidLetter:</span>
              <div className="flex-1">
                <NewsletterSignup variant="inline" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar: Support Our Mission (left) + Legal Links (center) + Copyright (right) */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Left: Support the SquidMission */}
          <Link to="/donate" className="text-primary hover:text-primary/80 transition-colors font-semibold flex items-center gap-2 text-sm">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            Support the SquidMission
          </Link>
          
          {/* Center: Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
            <span className="hidden md:inline">SQUIDEMY LLC • Honolulu, HI</span>
            <span className="hidden md:inline">•</span>
            <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
            <span>•</span>
            <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/squidprivacy" className="hover:text-primary transition-colors">SquidPrivacy</Link>
            <span>•</span>
            <Link to="/squidcookies" className="hover:text-primary transition-colors">Cookie Policy</Link>
            <span>•</span>
            <Link to="/pwyc-policy" className="hover:text-primary transition-colors">PWYC Policy</Link>
          </div>
          
          {/* Right: Copyright */}
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Squidemy LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
